import { useEffect, useRef, useState } from 'react';

export const prefersReducedMotion = () =>
  typeof window === 'undefined' || !window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const nextFrame = (callback) =>
  typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(callback) : window.setTimeout(() => callback(Date.now()), 16);
const cancelFrame = (id) => (typeof window.cancelAnimationFrame === 'function' ? window.cancelAnimationFrame(id) : window.clearTimeout(id));

/**
 * A number that counts to its new value (from zero on first show), like the
 * big totals in Apple Fitness. Reduced motion shows the value straight away.
 */
export const useCountUp = (value, { duration = 700 } = {}) => {
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? value : 0));
  const fromRef = useRef(shown);

  useEffect(() => {
    if (prefersReducedMotion()) {
      fromRef.current = value;
      setShown(value);
      return undefined;
    }
    const from = fromRef.current;
    const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
    let frame;
    const tick = (time) => {
      const progress = Math.min(((time || Date.now()) - start) / duration, 1);
      const current = from + (value - from) * (1 - (1 - progress) ** 3);
      fromRef.current = current;
      setShown(current);
      if (progress < 1) frame = nextFrame(tick);
    };
    frame = nextFrame(tick);
    return () => cancelFrame(frame);
  }, [value, duration]);

  return shown;
};
